import { useCallback, useMemo, useState } from "react";

import clsx from "clsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { AdLabelMatchesModal } from "@features/breakdown-v2/ad-label-matches-modal/components";
import { LabelRuleWizard } from "@features/breakdown-v2/label-rule-wizard/components";
import { useLabelWizard } from "@features/breakdown-v2/label-rule-wizard/hooks";
import {
  GET_LABEL_RULES_BY_GROUP_V3,
  GET_LABEL_RULE_GROUPS_V3,
  GET_LABEL_WIZARD_DATA_V3,
  UPDATE_LABEL_RULE_GROUP_V3,
} from "@gql/label-rules-v3";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type GetLabelRulesByGroupV3_me_labelRulesByGroupV3,
  type UpdateLabelRuleGroupV3,
  type UpdateLabelRuleGroupV3Variables,
} from "@north-beam/nb-client-side";
import {
  breakdownGroupToLabelRules,
  getNumberOfErrors,
  labelRulesToBreakdownGroup,
  validateBreakdownGroup,
  type BreakdownGroupDescriptor,
  type SegmentDescriptor,
} from "@north-beam/nb-common";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { notifyLabelsCache } from "@shared/notify-labels-cache";
import Spinner from "@shared/spinner";
import { Switch } from "@shared/switch";

import { useBlockNavigation } from "../../hooks";

import { ExitEditorComfirmationModal } from "./exit-editor-comfirmation-modal";
import { SwitchEditorComfirmationModal } from "./switch-editor-confirmation-modal";

interface Props {
  labelRules: GetLabelRulesByGroupV3_me_labelRulesByGroupV3[];
  deleting: boolean;
  duplicating: boolean;
  switchingToAdvancedEditor: boolean;
  duplicatingAndSwitchingToAdvancedEditor: boolean;
  onDelete: () => void;
  onDuplicate: () => void;
  onSwitchToAdvancedEditor: () => void;
  onDuplicateAndSwitchToAdvancedEditor: () => void;
}

export const SimpleEditor = ({
  labelRules,
  deleting,
  duplicating,
  switchingToAdvancedEditor,
  duplicatingAndSwitchingToAdvancedEditor,
  onDelete,
  onDuplicate,
  onSwitchToAdvancedEditor,
  onDuplicateAndSwitchToAdvancedEditor,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { labelNames: allLabelNames, labelFilters } = useLabelWizard();
  const { addToast } = useToasts();

  const [updateLabelRuleGroup, { loading: saving }] = useNorthbeamMutation<
    UpdateLabelRuleGroupV3,
    UpdateLabelRuleGroupV3Variables
  >(UPDATE_LABEL_RULE_GROUP_V3, {
    useCpuIntensive: true,
    refetchQueries: [
      { query: GET_LABEL_RULE_GROUPS_V3 },
      {
        query: GET_LABEL_RULES_BY_GROUP_V3,
        variables: { id: labelRules[0].groupId },
      },
      { query: GET_LABEL_WIZARD_DATA_V3 },
    ],
    awaitRefetchQueries: true,
  });

  const labelRuleName = labelRules[0].name;
  const labelNames = useMemo(
    () => allLabelNames.filter((e) => e !== labelRuleName),
    [allLabelNames],
  );

  const [breakdownGroup, setBreakdownGroup] =
    useState<BreakdownGroupDescriptor>(() =>
      labelRulesToBreakdownGroup(labelRules),
    );
  const [segmentToPreview, setSegmentToPreview] =
    useState<SegmentDescriptor | null>(null);
  const [isBreakdownModified, setIsBreakdownModified] = useState(false);
  const [showSwitchEditorConfirmModal, setShowSwitchEditorConfirmModal] =
    useState(false);
  const [showExitEditorConfirmModal, setShowExitEditorConfirmModal] =
    useState(false);
  const [blockedNavigationUrl, setBlockedNavigationUrl] = useState<
    string | null
  >(null);

  const validateAndUpdateBreakdownGroup = useCallback(
    (value: BreakdownGroupDescriptor) => {
      if (getNumberOfErrors(value) > 0) {
        setBreakdownGroup(validateBreakdownGroup(value, labelNames));
      } else {
        setBreakdownGroup(value);
      }
    },
    [labelNames],
  );

  useBlockNavigation({
    blockNavigation: isBreakdownModified,
    onBlock: (blockedUrl) => {
      setBlockedNavigationUrl(blockedUrl);
      setShowExitEditorConfirmModal(true);
    },
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <SecondaryButton
          className="flex items-center gap-2 font-bold"
          onClick={() => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onClick",
              additionalActionInfo:
                "Show exit breakdown edit page confirmation modal",
            });

            const desinationPath = user.pathFromRoot(
              `/breakdowns${location.search}`,
            );

            if (isBreakdownModified) {
              setShowExitEditorConfirmModal(true);
              setBlockedNavigationUrl(desinationPath);
            } else {
              navigate(desinationPath);
            }
          }}
        >
          <i className="fa-regular fa-arrow-left text-cosmic" /> BACK
        </SecondaryButton>
        <div className="flex items-center gap-2">
          <SecondaryButton
            className={clsx("flex items-center gap-2 font-bold", {
              "text-mars": !(
                deleting ||
                switchingToAdvancedEditor ||
                saving ||
                duplicating
              ),
            })}
            disabled={
              deleting || switchingToAdvancedEditor || saving || duplicating
            }
            onClick={() => onDelete()}
          >
            {deleting ? (
              <Spinner colorClassname="text-white-400" />
            ) : (
              <i className="fa-regular fa-trash text-mars" />
            )}
            DELETE
          </SecondaryButton>
          <SecondaryButton
            className="flex items-center gap-2 font-bold"
            disabled={
              deleting || switchingToAdvancedEditor || saving || duplicating
            }
            onClick={onDuplicate}
          >
            {duplicating ? (
              <Spinner colorClassname="text-white-400" />
            ) : (
              <i className="fa-regular fa-copy text-cosmic" />
            )}
            DUPLICATE
          </SecondaryButton>
          <PrimaryButton
            className="flex items-center gap-2 font-bold"
            disabled={
              deleting ||
              switchingToAdvancedEditor ||
              saving ||
              duplicating ||
              !isBreakdownModified ||
              getNumberOfErrors(breakdownGroup) > 0
            }
            onClick={async () => {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onClick",
                additionalActionInfo: "Update breakdown",
              });

              const validatedGroup = validateBreakdownGroup(
                breakdownGroup,
                labelNames,
              );
              setBreakdownGroup(validatedGroup);

              if (getNumberOfErrors(validatedGroup) === 0) {
                const rules = breakdownGroupToLabelRules(
                  validatedGroup,
                  labelFilters,
                );

                await updateLabelRuleGroup({
                  variables: {
                    id: labelRules[0].groupId,
                    campaignLabelRule: rules.campaign,
                    adsetLabelRule: rules.adset,
                    adLabelRule: rules.ad,
                  },
                });

                addToast(`Breakdown "${validatedGroup.name}" updated`, {
                  appearance: "success",
                });
                notifyLabelsCache();

                navigate(user.pathFromRoot(`/breakdowns${location.search}`));
              }
            }}
          >
            {saving ? (
              <Spinner colorClassname="text-white-400" />
            ) : (
              <i className="fa-regular fa-circle-plus" />
            )}
            SAVE
          </PrimaryButton>
        </div>
      </div>
      <div
        className={clsx(
          "bg-white border border-gray rounded-lg pt-6 pb-4 px-4",
          "min-h-[calc(100vh-56px-50px-48px)]",
        )}
      >
        <div className="float-right text-xs">
          <div className="flex items-center gap-2">
            <Switch
              id="switch-to-advanced-editor"
              value={false}
              onChange={() => {
                prettyLogEventPage({
                  domain: "Breakdown v2",
                  action: "onClick",
                  additionalActionInfo:
                    "Show switch to advanced editor confirmation modal",
                });

                setShowSwitchEditorConfirmModal(true);
              }}
            />
            Advanced (edit JSON)
          </div>
        </div>
        <LabelRuleWizard
          showSegmentActiveSwitch
          breakdownGroup={breakdownGroup}
          onChange={(value) => {
            validateAndUpdateBreakdownGroup(value);
            setIsBreakdownModified(true);
          }}
          onClickShowMatches={(value) => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onClick",
              additionalActionInfo: "Show preview matches modal",
              data: {
                pageContext: { segmentToPreview: value },
              },
            });

            setSegmentToPreview(value);
          }}
        />
      </div>
      <SwitchEditorComfirmationModal
        isOpen={showSwitchEditorConfirmModal}
        breakdownGroup={breakdownGroup}
        switching={switchingToAdvancedEditor}
        duplicating={duplicatingAndSwitchingToAdvancedEditor}
        onClose={() => {
          prettyLogEventPage({
            domain: "Breakdown v2",
            action: "onClick",
            additionalActionInfo:
              "Close switch to advanced editor confirmation modal",
          });

          setShowSwitchEditorConfirmModal(false);
        }}
        onConfirm={onSwitchToAdvancedEditor}
        onDuplicate={onDuplicateAndSwitchToAdvancedEditor}
      />
      <ExitEditorComfirmationModal
        isOpen={showExitEditorConfirmModal}
        onClose={() => {
          prettyLogEventPage({
            domain: "Breakdown v2",
            action: "onClick",
            additionalActionInfo: "Close exit confirm modal",
          });

          setShowExitEditorConfirmModal(false);
          setBlockedNavigationUrl(null);
        }}
        onConfirm={() => {
          prettyLogEventPage({
            domain: "Breakdown v2",
            action: "onClick",
            additionalActionInfo: "Confirm exit breakdown edit page",
          });

          if (blockedNavigationUrl) {
            setShowExitEditorConfirmModal(false);
            navigate(blockedNavigationUrl);
            setBlockedNavigationUrl(null);
          }
        }}
      />
      <AdLabelMatchesModal
        labelName={breakdownGroup.name}
        segments={breakdownGroup.segments}
        segment={segmentToPreview}
        onClose={() => {
          prettyLogEventPage({
            domain: "Breakdown v2",
            action: "onClick",
            additionalActionInfo: "Close preview matches modal",
          });

          setSegmentToPreview(null);
        }}
      />
    </div>
  );
};
